// Datadog RUM (Real User Monitoring) for the Flexigo app: which pages and features are used.
// Everything here is a no-op until `initRum()` has run on a known host (see deployment.js), so
// call sites never need to check. Nothing is collected until the user is identified: admins, and
// riders past the KVKK step. The SDK is not even loaded before that, so it never patches fetch, XHR
// or the console for visitors who are not tracked. See docs/agents/datadog-rum.md for the naming.
// Keep this module free of store and service imports: services/repository.js imports it.
import { getDeployment } from './deployment';
import { apiPath } from './scrub';
import { pageView, viewName } from './viewName';
import { menuItemRoute, menuLeaves, visibleMenuItems } from '../menu';
import createBeforeSend from './beforeSend';
import { rememberClickTarget } from './actionName';

const APPLICATION_ID = 'f7b9e957-fdc5-48c3-93ca-19376df5c8a2';
// Client tokens are public by design: they can only send events
const CLIENT_TOKEN = 'puba590970e3f9599ca78b269fcdae96940';
const SITE = 'datadoghq.com';
const SERVICE = 'flexigo-web';

// Streams that would keep a view "loading" for as long as it is open
const EXCLUDED_ACTIVITY_URLS = [/\/sse\/stream/, /\/assistant\//];
// Writes that say nothing about feature use: read-only POSTs that screens poll, and the grid layout
// and filter preferences that agGrid.vue saves on its own (user changes are `grid:*` actions)
const BACKGROUND_POSTS = [/\/vehicle\/positions$/, /\/route\/tracks$/, /\/user-preferences\//];
// Set by /auto-login before its SSO redirect; the shell replaces the SSO landing page with it
const AUTO_LOGIN_TARGET_KEY = 'flexigoAutoLoginTarget';

const SESSION_REPLAY_SAMPLE_RATE = 20;

let status = 'off'; // off | armed (known host, SDK not loaded) | loading | ready
let options = null;
let rum = null;
let pending = [];
let consented = false;
let consentAt = null;
let currentView = null;
let currentUserId = null;
// The menu the shell loaded before consent (riders at the KVKK step) and whose it is: sent if that
// user's collection starts on this page, dropped if someone else signs in
let heldMenu = null;
let lastSeenUserId = null;
let lastMenuExposure = null;

function run(fn) {
  if (status === 'ready') {
    try {
      fn(rum);
    } catch (error) {
      console.warn('[rum]', error);
    }
  } else if (status === 'armed' || status === 'loading') {
    pending.push(fn);
  }
}

function embedding(isKurogo) {
  if (isKurogo) return 'kurogo';
  return window.top !== window.self ? 'iframe' : 'none';
}

// Events from before consent (performance entries the browser buffered since page load, the login
// page's requests) are dropped; views cannot be, and Datadog ignores the return value for them
function isBeforeConsent(date) {
  return consentAt === null || date < consentAt;
}

// Called only while consented. The same menu for the same user is not counted twice.
function sendMenuExposure(items) {
  const key = `${currentUserId}|${items.join('|')}`;
  if (key === lastMenuExposure) return;
  lastMenuExposure = key;
  run((r) => r.addAction('menu:exposure', { menu: { items, count: items.length } }));
}

// Loads the SDK (its own chunk, so the other app entries never download it), on the first consent
function loadSdk() {
  if (status !== 'armed') return;
  status = 'loading';
  const { i18n, deployment, isKurogo } = options;
  import(/* webpackChunkName: "datadog" */ '@datadog/browser-rum')
    .then(({ datadogRum }) => {
      datadogRum.init({
        applicationId: APPLICATION_ID,
        clientToken: CLIENT_TOKEN,
        site: SITE,
        service: SERVICE,
        env: deployment.env,
        version: process.env.CODE_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: SESSION_REPLAY_SAMPLE_RATE,
        trackingConsent: 'not-granted',
        trackViewsManually: true,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        // uncompressed locally, so payloads can be read in the network panel
        compressIntakeRequests: deployment.env !== 'local',
        useSecureSessionCookie: window.location.protocol === 'https:',
        excludedActivityUrls: EXCLUDED_ACTIVITY_URLS,
        beforeSend: createBeforeSend(i18n, isBeforeConsent),
      });
      datadogRum.setGlobalContext({ deployment: deployment.deployment, embed: embedding(isKurogo) });
      rum = datadogRum;
      status = 'ready';
      const queued = pending;
      pending = [];
      queued.forEach(run);
    })
    .catch((error) => {
      status = 'off';
      pending = [];
      console.warn('[rum] Datadog RUM unavailable', error);
    });
}

/**
 * Turns RUM on for this page when the host is a known deployment. Never throws: an unguarded
 * failure here would stop the app from mounting (see the service worker note in main.js).
 * @param {{ i18n?: object, isKurogo?: boolean, deployment?: object }} [initOptions] `isKurogo`:
 *   running in a Kurogo host app's webview (`isKurogoWebview()`)
 */
export function initRum({ i18n, isKurogo = false, deployment = getDeployment() } = {}) {
  if (status !== 'off') return;
  try {
    if (!deployment || navigator.webdriver) return;
    options = { i18n, isKurogo, deployment };
    status = 'armed';
    window.addEventListener('pointerdown', rememberClickTarget, true);
    window.addEventListener('pointerup', rememberClickTarget, true);
    // The locale can change after boot (auto-login `lang`, RouteDetail `?lang=`)
    i18n?.vm?.$watch?.('locale', (locale) => run((r) => r.setGlobalContextProperty('ui_lang', locale)));
  } catch (error) {
    status = 'off';
    console.warn('[rum] Datadog RUM unavailable', error);
  }
}

// `a:<webAccountId>` for admins, `p:<personnelId>` for riders
function rumUserId(userDetails) {
  if (userDetails.accountType) {
    const id = userDetails.webAccountId ?? userDetails.accountId;
    return id == null ? undefined : `a:${id}`;
  }
  const id = userDetails.personnel?.id;
  return id == null ? undefined : `p:${id}`;
}

function withEmail(user, email) {
  return email ? { ...user, email } : user;
}

/**
 * Who the user is: id, type and email (the admin's login `uid`, the rider's personnel email), never
 * the name. Riders still at the KVKK step are not tracked (same condition as the ApproveKvkk
 * redirect in loginRedirect.js); riders whose company has no KVKK text have nothing to accept.
 * @param {object} userDetails `oauth/web/session/verify` response
 * @returns {{ id?: string, account_type: string, is_internal: boolean, email?: string } | null}
 */
export function rumUser(userDetails) {
  const { accountType } = userDetails;
  if (accountType) {
    return withEmail(
      {
        id: rumUserId(userDetails),
        account_type: accountType,
        is_internal: accountType === 'FLEXIGO_VEKTOR_ADMIN',
      },
      userDetails.uid,
    );
  }
  if (!userDetails.aggrementDate && userDetails.kvkkDocUrl?.length > 0) return null;
  return withEmail(
    { id: rumUserId(userDetails), account_type: 'RIDER', is_internal: false },
    userDetails.personnel?.email,
  );
}

/**
 * Called with every `SET_USER_DETAILS`; starts collection for users who may be tracked.
 * @param {object} userDetails
 */
export function identifyRumUser(userDetails) {
  if (status === 'off' || !userDetails || Array.isArray(userDetails) || !Object.keys(userDetails).length) return;
  lastSeenUserId = rumUserId(userDetails);
  if (heldMenu && heldMenu.userId !== lastSeenUserId) heldMenu = null;
  const user = rumUser(userDetails);
  if (!user) return;

  if (user.id && user.id !== currentUserId) {
    currentUserId = user.id;
    run((r) => r.setUser(user));
  }
  if (!consented) {
    consented = true;
    consentAt = Date.now();
    const locale = options.i18n?.locale;
    run((r) => {
      r.setGlobalContextProperty('ui_lang', locale);
      r.setTrackingConsent('granted');
    });
    if (currentView) {
      const view = currentView;
      run((r) => r.startView(view));
    }
    // The shell loads the menu once per page, so accepting KVKK in the app brings no new one
    if (heldMenu) {
      const { items } = heldMenu;
      heldMenu = null;
      sendMenuExposure(items);
    }
    loadSdk();
  }
}

/**
 * @param {object} companySettings `company-settings` response
 */
export function setRumCompany(companySettings) {
  if (status === 'off' || !companySettings?.companyId) return;
  const account = { id: String(companySettings.companyId), name: companySettings.companyTitle };
  run((r) => r.setAccount(account));
}

// After SSO the page loads on the SAML landing route before the shell moves to the stored target
function isSsoLandingPage(route) {
  try {
    const target = sessionStorage.getItem(AUTO_LOGIN_TARGET_KEY);
    return !!target && route.path !== target;
  } catch (error) {
    return false;
  }
}

/**
 * Starts a view when the page changes. Query changes and remounts of the same page are not
 * new views (the Flexigo shell keys its router-view on `fullPath`).
 * @param {object} route the route navigated to
 */
export function startPageView(route) {
  if (status === 'off') return;
  try {
    const view = pageView(route);
    if (!view || view.name === currentView?.name || isSsoLandingPage(route)) return;
    currentView = view;
    if (consented) run((r) => r.startView(view));
  } catch (error) {
    // runs in router.afterEach: a throw there would break navigation
    console.warn('[rum]', error);
  }
}

/**
 * Records a feature use as a custom action. Names come from a fixed vocabulary
 * (docs/agents/datadog-rum.md); never put names, emails, free text or ids in them.
 * @param {string} name
 * @param {object} [context]
 */
export function track(name, context) {
  if (status === 'off' || !consented) return;
  run((r) => r.addAction(name, context));
}

/**
 * Records a non-GET API call (`api:POST /commute/rest/v3/routes/:id/activate`), the broadest
 * signal of which features are used. `ok` is false for HTTP errors and for the backend's
 * HTTP 200 `{ error }` responses.
 * @param {object} config axios request config
 * @param {boolean} ok
 * @param {number|string} [httpStatus]
 */
export function trackApiCall(config, ok, httpStatus) {
  if (status === 'off' || !consented || !config) return;
  try {
    const method = String(config.method || 'get').toUpperCase();
    if (['GET', 'HEAD', 'OPTIONS'].includes(method)) return;
    const path = apiPath({ baseURL: config.baseURL || '', url: config.url || '' });
    if (BACKGROUND_POSTS.some((re) => re.test(path))) return;
    track(`api:${method} ${path}`, { api: { method, path, ok, status: httpStatus } });
  } catch (error) {
    // runs in the axios interceptors: a throw there would fail the request
    console.warn('[rum]', error);
  }
}

/**
 * Records which pages the user's menu offers, so "offered but never opened" can be found. A menu
 * loaded before consent is held and sent when collection starts.
 * @param {object} menu main menu tree (`SET_MAIN_MENU`)
 * @param {object} [companySettings] for the items the side menu hides
 */
export function trackMenuExposure(menu, companySettings) {
  if (status === 'off') return;
  const leaves = menuLeaves({ children: visibleMenuItems(menu, companySettings) });
  const items = [...new Set(leaves.map((item) => viewName(menuItemRoute(item))))];
  if (!items.length) return;
  if (consented) sendMenuExposure(items);
  else if (lastSeenUserId) heldMenu = { userId: lastSeenUserId, items };
}

/**
 * Ends the RUM session (logout, session expiry, tenant switch), so the next user or tenant on this
 * browser starts a new one.
 */
export function endRumSession() {
  if (status === 'off') return;
  consented = false;
  consentAt = null;
  currentView = null;
  currentUserId = null;
  heldMenu = null;
  lastSeenUserId = null;
  lastMenuExposure = null;
  run((r) => {
    r.clearUser();
    r.clearAccount();
    r.setTrackingConsent('not-granted');
  });
}
